import React, { useState, useEffect, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseView } from '@/components/base'
import { AppIcons } from '@/components/AppIcons'
import { useTheme } from '@/hooks/useTheme'
import { useDebounceCallBack } from '@/hooks/useDebounce'

interface SearchInputProps {
  withoutBorder?: boolean
  clean?: () => void
  defaultValue?: string
  value?: string
  onChangeText?: (text: string) => void
  onChangeTextDebounced?: (text: string) => void
  debounceDelay?: number
  searchRef?: React.RefObject<HTMLInputElement>
  placeholder?: string
  className?: string
  style?: React.CSSProperties
  contentContainerStyle?: React.CSSProperties
  onFocus?: () => void
  onBlur?: () => void
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      // withoutBorder = false, // unused
      clean,
      defaultValue,
      value: propValue,
      onChangeText,
      onChangeTextDebounced,
      debounceDelay = 1000,
      placeholder,
      className = '',
      style,
      contentContainerStyle,
      onFocus,
      onBlur,
    },
    ref,
  ) => {
    const [value, setValue] = useState(propValue ?? defaultValue ?? '')

    const theme = useTheme()
    const { t } = useTranslation()

    // Sync external value with internal state
    useEffect(() => {
      if (propValue !== undefined) {
        setValue(propValue)
      }
    }, [propValue])

    const handleBoxPress = () => {
      if (ref && typeof ref !== 'function') {
        ref.current?.focus()
      }
    }

    const handleFocus = () => {
      onFocus?.()
    }
    const handleBlur = () => {
      onBlur?.()
    }

    // Use the same debounce hook as React Native version
    const debounceOnChangeTextInput = useDebounceCallBack(
      onChangeTextDebounced || (() => {}),
      debounceDelay,
    )

    const handleClear = () => {
      setValue('')
      clean?.()
      onChangeText?.('')
      if (onChangeTextDebounced) {
        debounceOnChangeTextInput('')
      }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value
      setValue(text)
      onChangeText?.(text)

      if (onChangeTextDebounced) {
        debounceOnChangeTextInput(text)
      }
    }

    return (
      <BaseView
        className={`relative ${className}`}
        style={contentContainerStyle}
      >
        <BaseView
          bgColor="background.textInput"
          row
          align="center"
          gap={12}
          className="transition-all duration-200 ease-in-out cursor-pointer"
          onClick={handleBoxPress}
          style={{
            height: '56px',
            borderRadius: '16px',
            paddingLeft: '12px',
            paddingRight: '12px',
            borderWidth: 0,
            borderColor: 'transparent',
            borderStyle: 'solid',
            ...style,
          }}
        >
          {/* Search Icon */}
          <AppIcons
            name="search"
            size={24}
            color={value ? 'icon.activeInputIcon' : 'icon.disabledInputIcon'}
          />

          {/* Input */}
          <input
            ref={ref}
            type="text"
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder || t('form:placeholders.search')}
            className="flex-1 bg-transparent outline-none focus:outline-none"
            style={{
              color:
                theme.colors.label?.textTextInput || theme.colors.label.default,
              fontSize: '16px',
              fontFamily:
                'semibold, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              lineHeight: '20px',
              height: '100%',
              border: 'none',
              margin: 0,
              padding: 0,
              fontWeight: '500',
              outline: 'none',
              boxShadow: 'none',
            }}
          />

          {/* Clear Button */}
          {!!value && (
            <button
              onClick={handleClear}
              type="button"
              style={{
                backgroundColor:
                  theme.colors.icon?.roundedInputCleanButton ||
                  theme.colors.background.secondaryGray,
                width: '24px',
                height: '24px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                cursor: 'pointer',
                flexShrink: 0,
              }}
              tabIndex={-1}
              aria-label={t('translation:close') || 'Clear'}
            >
              <svg
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{
                  color: theme.colors.background.textInput,
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </BaseView>
      </BaseView>
    )
  },
)

SearchInput.displayName = 'SearchInput'

export default SearchInput
